import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import usePartner from '@/hooks/usePartner';
import { NewUserJoinedPartnerGroupEmailRequestDto } from '@/models/Admin/Emails/EmailRequestDtos';
import { PartnerGroupSelectModel } from '@/models/PartnerGroupSelectModel';
import { isEmail } from '@/utils/emailUtil';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import NewUserJoinedPartnerGroupForm from './NewUserJoinedPartnerGroupForm';
import { NewUserJoinedPartnerGroupSubmitState, PartnerGroupOption } from './types';

type Props = {
  onStateChange: React.Dispatch<React.SetStateAction<NewUserJoinedPartnerGroupSubmitState>>;
  resetSignal: number;
};

const createInitialForm = (): NewUserJoinedPartnerGroupEmailRequestDto => ({
  Email: '',
  NewUserEmail: '',
  PartnerGroupName: '',
});

const createEmptySubmitState = (): NewUserJoinedPartnerGroupSubmitState => ({
  request: createInitialForm(),
  isValid: false,
});

const NewUserJoinedPartnerGroupSection: React.FC<Props> = ({ onStateChange, resetSignal }) => {
  const { setAlert } = useContext(SnackbarContext);
  const { getPartnerGroupSelectOptions } = usePartner();

  const [form, setForm] = useState<NewUserJoinedPartnerGroupEmailRequestDto>(createInitialForm());
  const [partnerGroupOptions, setPartnerGroupOptions] = useState<PartnerGroupSelectModel[]>([]);

  useEffect(() => {
    const loadPartnerGroups = async () => {
      try {
        const options = await getPartnerGroupSelectOptions();
        setPartnerGroupOptions(options ?? []);
      } catch {
        setAlert({
          severity: 'error',
          message: 'Unable to load partner groups.',
        });
      }
    };

    void loadPartnerGroups();
  }, [getPartnerGroupSelectOptions, setAlert]);

  const partnerGroupAutocompleteOptions = useMemo<PartnerGroupOption[]>(() => {
    return partnerGroupOptions
      .map((partnerGroup) => {
        const partnerGroupName = partnerGroup.partnerGroupName?.trim() ?? '';
        const partnerGroupId = partnerGroup.partnerGroupId ?? null;

        if (partnerGroupName === '') {
          return null;
        }

        return {
          partnerGroupName,
          partnerGroupId,
          displayLabel:
            partnerGroupId == null
              ? partnerGroupName
              : `${partnerGroupName} (${String(partnerGroupId)})`,
        };
      })
      .filter((option): option is PartnerGroupOption => option != null)
      .sort((a, b) => a.displayLabel.localeCompare(b.displayLabel));
  }, [partnerGroupOptions]);

  const isValid =
    isEmail(form.Email) && isEmail(form.NewUserEmail) && form.PartnerGroupName.trim() !== '';

  useEffect(() => {
    onStateChange({
      request: form,
      isValid,
    });
  }, [form, isValid, onStateChange]);

  useEffect(() => {
    setForm(createInitialForm());
    onStateChange(createEmptySubmitState());
  }, [onStateChange, resetSignal]);

  return (
    <NewUserJoinedPartnerGroupForm
      form={form}
      setForm={setForm}
      partnerGroupAutocompleteOptions={partnerGroupAutocompleteOptions}
    />
  );
};

export default NewUserJoinedPartnerGroupSection;
